import { useMemo, useState } from "react";
import { Requirement, CourseSet } from "../../types/requirement.response";
import { AddableCourse, CourseLite, CourseOption, CourseSummary } from "../../types/course.response";
import { CourseRequirementOverride } from "../../services/api/user-courses.api";
import { BroadKind } from "../../utils/requirementResponseUtils";
import SectionCourseList from "./SectionCourseList";
import SuggestedCourseRows from "./SuggestedCourseRows";
import GroupOptionsModal from "./GroupOptionsModal";
import PanelProgress from "./PanelProgress";
import { PanelNode, buildPanelRows } from "./panelUtils";

const INITIAL_LIMIT = 3;

interface PanelViewProps {
    programId: string;
    requirement: Requirement;
    node: PanelNode;
    coursesMap: Record<string, CourseSummary>;
    relevantSubjects: string[];
    /** OPTION/OPTION_GROUP:渲染面板规则说明 + 已修课程展开 */
    showProgress: boolean;
    nodeFulfilled: boolean;
    /** LIST:课程很多时优先 intro 课的排序启发式 */
    introHeuristic: boolean;
    /**
     * broad requirement 的子形态(getBroadKind):
     *  - approval:文字提示替代课程列表(池不可枚举也无判定,搜索自加)
     *  - intensional:池 = 用户命中课,正常列表 + 推荐段
     *  - null:普通 requirement
     */
    broadKind: BroadKind | null;
    handleAddCourse: (newCourse: AddableCourse, overrides: CourseRequirementOverride[], semester: string | null, classSectionIds?: number[]) => void;
    handleDeleteCourse: (deletedCourse: CourseLite) => void;
    handleFavoriteCourse: (courseOption: CourseLite) => void;
    handleOverride: (course: CourseLite, override: CourseRequirementOverride) => void | Promise<void>;
    optimisticFavorites: Map<string, boolean>;
    userCourses?: CourseOption[];
    onSeeAll: () => void;
    onOptionAdd: (course: AddableCourse, nodeId: string | null) => void | Promise<void>;
}

/**
 * 渲染单个面板:可选的规则说明 + 课程行 + 组课程弹窗。
 * 调用方用 key={node.id} 让切 tab 时 visibleLimit/optionsGroup 等状态自动重置。
 */
export default function PanelView({
    programId,
    requirement,
    node,
    coursesMap,
    relevantSubjects,
    showProgress,
    nodeFulfilled,
    introHeuristic,
    broadKind,
    handleAddCourse,
    handleDeleteCourse,
    handleFavoriteCourse,
    handleOverride,
    optimisticFavorites,
    userCourses,
    onSeeAll,
    onOptionAdd,
}: PanelViewProps) {
    const [visibleLimit, setVisibleLimit] = useState(INITIAL_LIMIT);
    const [optionsGroup, setOptionsGroup] = useState<CourseSet | null>(null);

    const { courses, optionCountByKey, groupByKey, groupMode } = useMemo(
        () => buildPanelRows(node, coursesMap, relevantSubjects, { introHeuristic }),
        [node, coursesMap, relevantSubjects, introHeuristic]
    );

    // broad 两段式只发生在 LIST 根面板(v1 唯一形态);OPTION/GROUP 面板行为不变
    const showApprovalNotice = broadKind === "approval" && !groupMode && !showProgress;
    const showBroadSegments = broadKind === "intensional" && !groupMode && !showProgress;

    return (
        <>
            {showProgress && (
                <PanelProgress
                    requirement={requirement}
                    coursesMap={coursesMap}
                    node={node}
                    nodeFulfilled={nodeFulfilled}
                    onAdd={onOptionAdd}
                    onDelete={handleDeleteCourse}
                    onOverride={handleOverride}
                />
            )}

            <table className={`w-full border-collapse ${showProgress ? "mt-2" : "mt-4"}`}>
                <tbody>
                    {showApprovalNotice ? (
                        <tr>
                            <td colSpan={4} className="py-4">
                                <span className="text-sm text-gray-400 italic">This requirement covers a broad range of courses. We're unable to display all eligible courses or automatically verify completion—please review it manually.</span>
                            </td>
                        </tr>
                    ) : (
                        <SectionCourseList
                            courses={courses}
                            requirementId={requirement.info.id}
                            requirementName={requirement.info.name}
                            visibleLimit={visibleLimit}
                            initialLimit={INITIAL_LIMIT}
                            handleAddCourse={handleAddCourse}
                            handleDeleteCourse={handleDeleteCourse}
                            handleFavoriteCourse={handleFavoriteCourse}
                            optimisticFavorites={optimisticFavorites}
                            userCourses={userCourses}
                            optionCountByKey={groupMode ? optionCountByKey : undefined}
                            showMoreLabel={groupMode ? "Show more groups" : undefined}
                            onShowOptions={groupMode ? (mapKey) => setOptionsGroup(groupByKey.get(mapKey) ?? null) : undefined}
                            onShowMore={() => setVisibleLimit(prev => prev + 5)}
                            onSeeAll={onSeeAll}
                            emptyMessage={showBroadSegments
                                ? "Courses you take that match this requirement will show up here."
                                : undefined}
                        />
                    )}
                    {showBroadSegments && (
                        <SuggestedCourseRows
                            programId={programId}
                            requirement={requirement}
                            handleAddCourse={handleAddCourse}
                            handleDeleteCourse={handleDeleteCourse}
                            handleFavoriteCourse={handleFavoriteCourse}
                            optimisticFavorites={optimisticFavorites}
                            userCourses={userCourses}
                        />
                    )}
                </tbody>
            </table>

            {optionsGroup && (
                <GroupOptionsModal
                    courseSet={optionsGroup}
                    coursesMap={coursesMap}
                    relevantSubjects={relevantSubjects}
                    requirementId={requirement.info.id}
                    handleAddCourse={handleAddCourse}
                    handleDeleteCourse={handleDeleteCourse}
                    handleFavoriteCourse={handleFavoriteCourse}
                    optimisticFavorites={optimisticFavorites}
                    userCourses={userCourses}
                    onClose={() => setOptionsGroup(null)}
                />
            )}
        </>
    );
}
